import * as immutable from 'object-path-immutable'
import React, { useReducer, useEffect } from 'react'

import { ProfessionalsList } from './ProfessionalsList'
import { Messages } from '../lib/ui/Messages'
import { authedGet } from '../lib/util/http'
import { AVProfessionalProject } from '../types/entities'
import { Action, User } from '../types/app'
import {
    ProfessionalProjectSortables,
    DashboardPayload,
} from '../types/dashboard'
import { routeBackToDashboard } from '../routing'

export interface DashboardState {
    messages?: []
    professionals: AVProfessionalProject[]
    userProfessionals: AVProfessionalProject[]
    sorting: DashboardSorting
    filtering: string
}

type LodashSortType = 'asc' | 'desc'

interface DashboardSorting {
    property: ProfessionalProjectSortables
    order: LodashSortType
}

interface SortingFiltering {
    sortProperty: ProfessionalProjectSortables
    sortOrder: LodashSortType
    filter: string
}

const applySortingAndFiltering = (
    state: DashboardState,
    professionals: AVProfessionalProject[] | null,
    sortProperty: string,
    sortOrder: LodashSortType,
    filter: string
) => {
    return immutable
        .wrap(state)
        .set(['professionals'], professionals)
        .set(['sorting'], {
            property: sortProperty,
            order: sortOrder,
        })
        .set('filtering', filter)
        .value()
}

const toggleSortProfessionalsByProperty = (
    state: DashboardState,
    property: ProfessionalProjectSortables
): DashboardState => {
    let newOrder: LodashSortType = state.sorting.order
    let newProperty: ProfessionalProjectSortables = state.sorting.property

    if (state.sorting.property === property) {
        // toggle order
        newOrder = state.sorting.order === 'desc' ? 'asc' : 'desc'
    } else {
        // sort by different property
        newProperty = property
    }
    return applySortingAndFiltering(
        state,
        state.professionals,
        newProperty,
        newOrder,
        state.filtering
    )
}

const filterProfessionals = (state: DashboardState, query: string) => {
    return applySortingAndFiltering(
        state,
        state.professionals,
        state.sorting.property,
        state.sorting.order,
        query
    )
}

const setSortContractsByProperty = (
    state: DashboardState,
    message: SortingFiltering
) => {
    return applySortingAndFiltering(
        state,
        state.professionals,
        message.sortProperty,
        message.sortOrder,
        message.filter
    )
}

type LocalStorageSortFilter = {
    AvtDashboardSorting: string
    AvtDashboardFilter: string
}

const mapPayloadToSorting = (
    sortFilter: LocalStorageSortFilter
): SortingFiltering => {
    const sortPair = sortFilter.AvtDashboardSorting.split(' ')
    return {
        sortProperty: sortPair[0] as ProfessionalProjectSortables,
        sortOrder: sortPair[1] as LodashSortType,
        filter: sortFilter.AvtDashboardFilter,
    }
}

const handleProfessionalProjectsLoaded = (
    state: DashboardState,
    payload: any
) => {
    if (payload.status === 'FAILED') {
        return immutable.set(state, ['messages'], [payload.message])
    }
    return applySortingAndFiltering(
        state,
        payload,
        state.sorting.property,
        state.sorting.order,
        state.filtering
    )
}

const loadProfessionalProjects = async (dispatch: any) => {
    const contractsResult = await authedGet(`av/professionals`)
    const json = await contractsResult.json()
    dispatch({ name: 'PROFESSIONALS_LOADED', payload: json.data })
}

const saveSortingFiltering = async (state: DashboardState) => {
    const avtSorting = `${state.sorting.property} ${state.sorting.order}`
    const request = {
        AvtDashboardSorting: avtSorting,
        AvtDashboardFilter: state.filtering,
    }
    localStorage.setItem('sortFilter', JSON.stringify(request))
}

const reducer: React.Reducer<DashboardState, Action> = (
    state: DashboardState,
    action: Action
) => {
    switch (action.name) {
        case 'PROFESSIONALS_LOADED':
            return handleProfessionalProjectsLoaded(state, action.payload)
        case 'PROFESSIONALS_FILTER':
            return filterProfessionals(state, action.payload)
        case 'PROFESSIONALS_SORT':
            return toggleSortProfessionalsByProperty(
                state,
                (action.payload as DashboardPayload).property
            )
        case 'USER_LOADED':
            // try set sorting and filtering from localStorage
            const sortFilter = localStorage.getItem('sortFilter')
            if (sortFilter) {
                const json = JSON.parse(sortFilter)
                return setSortContractsByProperty(
                    state,
                    mapPayloadToSorting(json)
                )
            }
            return state
        default:
            console.error(`Invalid action: `, action)
            return state
    }
}

const initialState: DashboardState = {
    professionals: [],
    userProfessionals: [],
    sorting: {
        property: 'LastName',
        order: 'asc',
    },
    filtering: '',
}

export const Dashboard = (props: any) => {
    const [state, dispatch] = useReducer<React.Reducer<DashboardState, Action>>(
        reducer,
        initialState
    )

    useEffect(() => {
        loadProfessionalProjects(dispatch)
    }, [])

    useEffect(() => {
        const user = props.user as User
        dispatch({ name: 'USER_LOADED', payload: user })
    }, [props.user])

    useEffect(() => {
        saveSortingFiltering(state)
    }, [state.filtering, state.sorting.property, state.sorting.order])

    if (state.professionals.length === 0) {
        return <div>Loading..</div>
    }
    return (
        <div>
            <div className="top-nav">
                <button
                    className="btn-back"
                    title="Back"
                    onClick={() => routeBackToDashboard()}
                ></button>
            </div>
            <div className="white bg-red head">COPRA Management Tool</div>
            {state.messages && <Messages messages={state.messages} />}
            <ProfessionalsList
                setSorting={(property: string) => {
                    dispatch({
                        name: 'PROFESSIONALS_SORT',
                        payload: { property },
                    })
                }}
                setFiltering={(query: string) => {
                    dispatch({ name: 'PROFESSIONALS_FILTER', payload: query })
                }}
                professionals={state.professionals}
                userProfessionals={state.userProfessionals}
                sorting={state.sorting}
                filtering={state.filtering}
            />
        </div>
    )
}
